import {
  type CSSProperties,
  type PointerEventHandler,
  type PropsWithChildren,
  type WheelEvent,
  forwardRef,
  useCallback,
  useRef,
} from "react";
import { useMove } from "react-aria";

import { useDOMRef } from "widgets/hooks";

import { useTrackRefSize } from "./hooks/useTrackSize.js";
import { MIN_VIEW_RANGE_SIZE, SCALE_FACTOR_MOUSE_WHEEL_ZOOM } from "./new/_mod.js";
import { type TrackRange } from "./new/useViewerGraphql.js";

interface TrackInteractiveSurface {
  contigLength: number; // Used for normalization and trim the range
  range: TrackRange;
  setRange?: (range: TrackRange) => void;

  // Both for the "yellow" and "blue" rectangle
  // Genome Viewer only, not for usage when a single track is shown
  // Bound to the Genome Viewer store
  setZoomRange?: (range: TrackRange | null) => void;
  setSelectionRange?: (range: TrackRange | null) => void;

  className?: string;
  style?: CSSProperties;
}

/**
 * Used in the background of the track to catch pointer event in order to zoom and or select in the current Range
 * Avoid to put any overlay widgets inside this component as React passes the events into any children, even in a Portal.
 * That will prevent to select in an input for instance. Or you could drag the track from within a Dialog.
 */
export const TrackInteractiveSurface = forwardRef<HTMLDivElement, PropsWithChildren<TrackInteractiveSurface>>(
  function TrackInteractiveSurface(props, ref) {
    const trackRef = useDOMRef<HTMLDivElement>(ref);
    const { width, baseWidth } = useTrackRefSize(trackRef, props.range);
    const pointerPosition = useRef(-1);

    // Data used to draw the rectangles
    const startPosition = useRef(-1);
    const moveDelta = useRef(0);

    const pixelToBasePosition = useCallback<(x: number) => number>(
      (x) => props.range.start + Math.floor(x / baseWidth),
      [baseWidth, props.range.start],
    );

    const { moveProps: trackMoveProps } = useMove({
      onMoveStart() {
        // If you move and comes back to the exact same position, we may have a false positive.
        // So everytime we move, we prevent the falsy detection by resetting the pointerPosition to default.
        // The onMoveStart happens after onPointerDown.
        pointerPosition.current = -1;
      },
      onMove(e) {
        if (e.metaKey || e.altKey) {
          // Will move the rectangle instead of making it bigger
          // Would prefer to use "Space" here, but react-aria doesn't expose the Event API :-/
          startPosition.current += e.deltaX;
        } else {
          // Make the rectangles bigger or smaller.
          moveDelta.current += e.deltaX;
        }

        // Zoom or Selection
        if (e.ctrlKey || e.shiftKey) {
          const startBase = pixelToBasePosition(startPosition.current);
          const stopBase = pixelToBasePosition(startPosition.current + moveDelta.current);
          const newZoomOrSelectionRange = {
            start: Math.max(props.range.start, Math.min(startBase, stopBase)),
            stop: Math.min(props.range.stop, Math.max(startBase, stopBase)),
          };

          // Zoom (Yellow rectangle)
          if (e.ctrlKey) {
            props.setZoomRange?.(newZoomOrSelectionRange);
            return;
          }

          // Selection (Blue Rectangle)
          if (e.shiftKey) {
            props.setSelectionRange?.(newZoomOrSelectionRange);
            return;
          }
        }

        // If not keyboard keys are pressed, then we just move the range.
        // Unlike for the selection, the move is inverted
        const moveBaseDelta = pixelToBasePosition(-moveDelta.current);
        const rangeDelta = props.range.stop - props.range.start;
        const rangeStart = Math.min(Math.max(moveBaseDelta, 1), props.contigLength - rangeDelta);
        const rangeStop = rangeStart + rangeDelta;

        props.setRange?.({ start: rangeStart, stop: rangeStop });
      },
      onMoveEnd(e) {
        const startBase = pixelToBasePosition(startPosition.current);
        const stopBase = pixelToBasePosition(startPosition.current + moveDelta.current);
        const newRange = {
          start: Math.max(props.range.start, Math.min(startBase, stopBase)),
          stop: Math.min(props.range.stop, Math.max(startBase, stopBase)),
        };

        // Zoom (Yellow rectangle)
        if (e.ctrlKey) {
          props.setRange?.(newRange);
        }

        // Reset all
        // We don't want to reset the mouse position,
        // because if we do so, the selection rectangle will be broken.
        moveDelta.current = 0;
        startPosition.current = -1;
        props.setZoomRange?.(null);

        if (e.shiftKey) {
          // FIXME: Change the selection Model link (no genomeViewer store anymore)
          // dispatch(updateSelectionModelFromRange(newRange));
        }
      },
    });

    // Register the current mouse position before the drag event => Selection modes.
    const handlePointerDown = useCallback<PointerEventHandler<HTMLDivElement>>(
      (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        startPosition.current = x > 0 ? x : 0;
        pointerPosition.current = event.clientX;

        // if (!(event.ctrlKey || event.metaKey || event.altKey)) {
        //   // reset
        //   props.setZoomRange?.(null);
        //   props.setSelectionRange?.(null);
        // }
      },
      [props.setSelectionRange, props.setZoomRange],
    );

    const handlePointerUp = useCallback<PointerEventHandler<HTMLDivElement>>(
      (event) => {
        // If no move, we remove the selection rectangle
        if (pointerPosition.current === event.clientX) {
          props.setSelectionRange?.(null);
        }
        pointerPosition.current = -1;
      },
      [props.setSelectionRange],
    );

    const handleMouseWheel = useCallback(
      (event: WheelEvent<HTMLDivElement>) => {
        if (event.shiftKey || event.ctrlKey || event.metaKey) {
          // deltaY: zoom in (wheel up) -> negative, zoom out (wheel down) -> positive
          const finalFactor = event.deltaY > 0 ? SCALE_FACTOR_MOUSE_WHEEL_ZOOM : 1 / SCALE_FACTOR_MOUSE_WHEEL_ZOOM;
          // number of pixels from the left border of the browser window to the left border of the first displayed nucleotide (aka base)
          const leftOffset = event.currentTarget.getBoundingClientRect().left;
          // the relative pixel position in the track
          const xPos = Math.min(Math.max(0, event.clientX - leftOffset), width);

          // the index of the base that is currently below the mouse pinter
          const currentBase = pixelToBasePosition(xPos);

          // Find range for the new zoom level

          // Calculate the new range based on an approximate value for newPxPerBp
          let newPxPerBp = baseWidth / finalFactor;
          let newRange = getNewRange(newPxPerBp, currentBase, xPos, width);

          // Above calculation approximates the baseWidthPx from the scaling factor
          // but the actual space for a base is different because the display will show complete bases while the approximate
          // width would produce a partially displayed base at the end or beginning of the display range.
          // Let's recalculate everything with a better value for newPxPerBp
          newPxPerBp = width / (newRange.stop - newRange.start + 1);
          newRange = getNewRange(newPxPerBp, currentBase, xPos, width);

          // Above calculations get the length of range for the final scale right, but the current base
          // might be no longer under the mouse.
          // To compensate we shift the displayed range by full bases until the current base is under the mouse again.
          //
          // Check if the current xPos of the mouse would still be over the correct base in the new range
          const baseAtMx = Math.floor(xPos / newPxPerBp) + newRange.start;
          // calculate the number of bases that we need to shift the new range so that the base is below the mouse again
          const deltaBp = currentBase - baseAtMx;
          // shift the new range by the deltaBp
          newRange = { start: newRange.start + deltaBp, stop: newRange.stop + deltaBp };

          // check if newRange is still within limits
          if (newRange.stop - newRange.start + 1 < MIN_VIEW_RANGE_SIZE) {
            return;
          }

          props.setRange?.(newRange);
        }
      },
      [baseWidth, width, pixelToBasePosition],
    );

    const interactiveProps = {
      onWheel: handleMouseWheel,
      ...trackMoveProps,
      onPointerDown(event) {
        handlePointerDown(event);
        trackMoveProps.onPointerDown?.(event);
      },
      onPointerUp(event) {
        handlePointerUp(event);
        trackMoveProps.onPointerUp?.(event);
      },
    };

    return (
      // <div ref={trackRef} {...interactiveProps} className={props.className} style={props.style}>
      <div ref={trackRef} className={props.className} style={props.style}>
        {props.children}
      </div>
    );
  },
);

function getNewRange(newPxPerBp: number, currentBase: number, xPos: number, trackWidth: number): TrackRange {
  const newLeftBp = Math.floor(xPos / newPxPerBp);
  const newPxOnRightSide = trackWidth - (newLeftBp + 1) * newPxPerBp;
  const newRightBp = Math.floor(newPxOnRightSide / newPxPerBp);
  return { start: currentBase - newLeftBp, stop: currentBase + newRightBp };
}
